import { WORLD_COUNTRIES } from "@/lib/countries";

export type AccountType = "INDIVIDUAL" | "BUSINESS";
export type CountryCode = string;
export type Currency = string;
export type TxOperation =
  | "DEPOSIT"
  | "WITHDRAWAL"
  | "TRANSFER_OUT"
  | "TRANSFER_IN"
  | "FX_CONVERSION"
  | "COLLECTION"
  | "PAYIN"
  | "PAYOUT"
  | "REFUND"
  | "NOTIFICATION_PLAN_SUBSCRIPTION";
export type TxStatus = "COMPLETED" | "PENDING" | "PROCESSING" | "FAILED";
export type Method = string;

export interface Country {
  code: CountryCode;
  name: string;
  currency: Currency;
  dial: string;
  methods: Method[];
}

export interface Wallet {
  id: string;
  country: CountryCode;
  currency: Currency;
  type: "MAIN" | "SAVINGS" | "ESCROW" | "RESERVE";
  scope: "LOCAL" | "INTERNATIONAL";
  status: "ACTIVE" | "PENDING" | "SUSPENDED" | "CLOSED";
  balance: number;
}

export interface Tx {
  reference: string;
  walletId: string;
  country: CountryCode;
  currency: Currency;
  operation: TxOperation;
  status: TxStatus;
  amount: number;
  fee: number;
  method: Method;
  counterparty: string;
  description: string;
  createdAt: string;
}

export interface Notice {
  id: string;
  title: string;
  body: string;
  type: "PAYMENT" | "COMPLIANCE" | "SYSTEM" | string;
  status?: string;
  read: boolean;
  createdAt: string;
}

const LEGACY_COUNTRIES: Country[] = [
  {
    code: "CM",
    name: "Cameroun",
    currency: "XAF",
    dial: "+237",
    methods: ["mtn", "orange"],
  },
  {
    code: "CI",
    name: "Côte d'Ivoire",
    currency: "XOF",
    dial: "+225",
    methods: ["mtn", "orange"],
  },
  {
    code: "SN",
    name: "Sénégal",
    currency: "XOF",
    dial: "+221",
    methods: ["orange", "wave"],
  },
  {
    code: "BJ",
    name: "Bénin",
    currency: "XOF",
    dial: "+229",
    methods: ["mtn"],
  },
  {
    code: "GA",
    name: "Gabon",
    currency: "XAF",
    dial: "+241",
    methods: ["orange"],
  },
  {
    code: "CG",
    name: "Congo",
    currency: "XAF",
    dial: "+242",
    methods: ["mtn"],
  },
  {
    code: "CD",
    name: "RD Congo",
    currency: "CDF",
    dial: "+243",
    methods: ["orange"],
  },
  {
    code: "GN",
    name: "Guinée",
    currency: "GNF",
    dial: "+224",
    methods: ["orange", "mtn"],
  },
  {
    code: "INT",
    name: "International",
    currency: "USD",
    dial: "",
    methods: ["card"],
  },
];

// Geographic metadata is not a list of supported payment markets.
export const COUNTRIES: Country[] = [
  ...WORLD_COUNTRIES.map((country) => ({
    ...country,
    currency:
      LEGACY_COUNTRIES.find((c) => c.code === country.code)?.currency ?? "",
    methods: [],
  })),
  LEGACY_COUNTRIES.find((c) => c.code === "INT")!,
];

export function countryOf(code: CountryCode): Country {
  return (
    COUNTRIES.find((country) => country.code === code) ?? {
      code,
      name: code,
      currency: "",
      dial: "",
      methods: [],
    }
  );
}

export const METHOD_LABEL: Record<string, string> = {
  afrimoney: "Afri Money",
  airtel: "Airtel Money",
  amanata: "Amanata",
  card: "Carte bancaire",
  celtiis: "Celtiis Money",
  coris: "Coris Money",
  emoney: "E-money",
  free: "Free Money",
  internal: "Transfert interne",
  mobile_money: "Mobile Money",
  mpesa: "M-Pesa",
  moov: "Moov Money",
  mtn: "MTN Money",
  nita: "Nita",
  orange: "Orange Money",
  vodacom: "Vodacom",
  wave: "Wave",
  wligdicash: "LigdiCash",
  yas: "Mixx By Yas",
  zamani: "Zamani",
  zamtel: "Zamtel Money",
};

export const OP_LABEL: Record<TxOperation, string> = {
  COLLECTION: "Encaissement",
  DEPOSIT: "Dépôt",
  FX_CONVERSION: "Conversion",
  PAYIN: "Encaissement",
  PAYOUT: "Décaissement",
  REFUND: "Remboursement",
  TRANSFER_IN: "Transfert reçu",
  TRANSFER_OUT: "Transfert envoyé",
  WITHDRAWAL: "Retrait",
  NOTIFICATION_PLAN_SUBSCRIPTION: "Abonnement notifications",
};

export const STATUS_LABEL: Record<TxStatus, string> = {
  COMPLETED: "Terminée",
  FAILED: "Échouée",
  PENDING: "En attente",
  PROCESSING: "En traitement",
};

export const OUTGOING: TxOperation[] = [
  "WITHDRAWAL",
  "TRANSFER_OUT",
  "PAYOUT",
  "REFUND",
];

export function fmt(amount: number, currency: Currency): string {
  const digits = currency === "USD" ? 2 : 0;
  return `${new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(amount)} ${currency}`;
}

export function fmtDate(iso: string, withTime = true): string {
  return new Intl.DateTimeFormat(
    "fr-FR",
    withTime
      ? { day: "2-digit", hour: "2-digit", minute: "2-digit", month: "short" }
      : { day: "2-digit", month: "short", year: "numeric" },
  ).format(new Date(iso));
}

export const signed = (transaction: Tx): number =>
  OUTGOING.includes(transaction.operation) ? -1 : 1;
